import { useMemo, useState } from "react";
import { games, type CanonicalGame } from "@hanjan-shieo/content";
import { isVerifiedArcadeGame, verifiedArcadeGameIds } from "@hanjan-shieo/domain";
import { Button, Icon } from "@hanjan-shieo/ui";
import { arcadeCardCopy, GamePreview, type VerifiedArcadeId } from "./GamePreview";
import { Link, navigate, track } from "./platform";
import { rankedGameIds } from "./localData";
import { usePartyRuntime } from "./partyRuntime";
import { PortfolioThemeControl } from "./PortfolioThemeControl";

const verifiedGames = verifiedArcadeGameIds
  .map((id) => games.find((game) => game.canonicalId === id))
  .filter((game): game is CanonicalGame => Boolean(game && isVerifiedArcadeGame(game)));

type ArcadeCategory = "all" | "number" | "word" | "role" | "chance";
const categories:Record<ArcadeCategory,{name:string;ids:readonly VerifiedArcadeId[]}>= {
  all:{name:"전체",ids:verifiedArcadeGameIds},
  number:{name:"숫자·순발력",ids:["three-six-nine","baskin-31","nunchi-count"]},
  word:{name:"말·퀴즈",ids:["categories","three-syllable-chain","word-association","year-quiz"]},
  role:{name:"역할·마음",ids:["same-word-sync","mafia-local"]},
  chance:{name:"운·연쇄",ids:["open-dice-rules","bouncing-ball-chant"]},
};

function byId(id: string) {
  return verifiedGames.find((game) => game.canonicalId === id);
}

function ArcadeCard({ game, compact = false }: { game: CanonicalGame; compact?: boolean }) {
  const id = game.canonicalId as VerifiedArcadeId;
  const copy = arcadeCardCopy[id];
  return <article className={`arcade-card arcade-card--verified ${compact ? "arcade-card--compact" : ""}`} data-game={id}>
    <GamePreview id={id} compact={compact}/>
    <div className="arcade-card__body">
      <div className="arcade-card__meta"><span>직접 플레이</span><span>{copy.label} · {game.playersMin}명+</span></div>
      <h3>{game.displayName}</h3>
      <p>{copy.hook}</p>
      <div className="arcade-card__footer"><span>{copy.label} · {copy.result}</span><Link href={`/play/local?game=${game.canonicalId}&quick=1`}>바로 시작 <span aria-hidden="true">→</span></Link></div>
    </div>
  </article>;
}

function Shelf({ title, description, items }: { title: string; description: string; items: CanonicalGame[] }) {
  return <section className="arcade-section"><header><div><h2>{title}</h2><p>{description}</p></div><span>{items.length}개</span></header><div className="arcade-shelf">{items.map((game) => <ArcadeCard key={game.canonicalId} game={game}/>)}</div></section>;
}

export function Arcade() {
  const { data, session } = usePartyRuntime();
  const ranked = rankedGameIds(data).map(byId).filter((game): game is CanonicalGame => Boolean(game)).slice(0, 5);
  const recent = data.recentGameIds.slice().reverse().map(byId).filter((game): game is CanonicalGame => Boolean(game)).slice(0, 6);
  const lead = ranked[0] ?? verifiedGames[0]!;
  const leadId = lead.canonicalId as VerifiedArcadeId;
  const [query, setQuery] = useState("");
  const [category,setCategory] = useState<ArcadeCategory>("all");
  const categoryGames = categories[category].ids.map(byId).filter((game):game is CanonicalGame=>Boolean(game));
  const matchedGames = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase("ko");
    if (!normalized) return [];
    return verifiedGames.filter((game) => [game.displayName, ...game.aliases, ...game.searchAliases].some((name) => name.toLocaleLowerCase("ko").includes(normalized)));
  }, [query]);

  return <div className="arcade-page">
    <section className="arcade-hero">
      <div className="arcade-hero__copy">
        <div className="arcade-hero__kicker"><span className="eyebrow">게임룸 · 지금 바로 시작</span><span>11개 직접 플레이</span></div>
        <h1>모였으면,<br/><em>한 판부터.</em></h1>
        <p>한 휴대폰을 돌려 답을 고르고, 친구들과 결과를 함께 확인해요. 가입 없이 바로 시작할 수 있어요.</p>
        <div className="arcade-hero__actions">
          <Button onClick={() => { track("recommendation_accepted", { canonicalId: lead.canonicalId, surface: "arcade_lead" }); navigate(`/play/local?game=${lead.canonicalId}&quick=1`); }}>{ranked.length ? "이 기기 1위 바로 시작" : `${lead.displayName} 바로 시작`}</Button>
          <Link href="/room/new">친구들 방 만들기</Link>
        </div>
        <small className="arcade-hero__note">한 기기에서 바로 하거나, 링크로 모여 같은 판을 시작할 수 있어요.</small>
      </div>
      <div className="arcade-lead" aria-label={`${lead.displayName} 빠른 시작`}>
        <div className="arcade-lead__top"><span>{ranked.length ? "이 기기 인기 1위" : "첫 판 추천"}</span><b>01</b></div>
        <GamePreview id={leadId}/>
        <h2>{lead.displayName}</h2>
        <small>{arcadeCardCopy[leadId].hook}</small>
      </div>
    </section>

    <PortfolioThemeControl />
    <section className="room-launcher"><div><h2>{session && !session.endedAt ? "하던 모임 이어가기" : "오늘 모임을 이어서 놀기"}</h2><p>인원과 조용한 자리 조건을 정하고, 실제 결과를 다음 모임에도 다시 꺼내요.</p></div><div className="room-launcher__actions">{session && !session.endedAt && <Link href={`/play/local?game=${session.selectedGameId}`}>하던 판 계속하기</Link>}<Link href="/gathering">모임 설정·진행</Link><Link href="/recap">지난 모임 기록</Link></div></section>

    <section className="room-launcher" aria-label="친구와 같은 방에서 하기">
      <div><span className="eyebrow">호스트 화면으로 함께 플레이</span><h2>링크로 모여 같은 게임 고르기</h2><p>한 명이 방을 만들고 링크를 보내면, 모두 준비되면 호스트 한 기기로 진행하고, 각자 화면에서 공개 결과를 확인해요.</p></div>
      <div className="room-launcher__actions"><Button onClick={() => navigate("/room/new")}>새 방 만들기</Button><button className="button button--ghost" onClick={() => navigate("/room/join")}>코드로 참여</button></div>
    </section>

    <section className="arcade-section popularity-section">
      <header><div><h2>이 기기 인기 순위</h2><p>이 휴대폰에서 실제로 시작하고 끝낸 횟수만 반영해요.</p></div><span>{ranked.length ? `${ranked.length}개 기록` : "기록 없음"}</span></header>
      {ranked.length ? <ol className="ranking-list">{ranked.map((game, index) => <li key={game.canonicalId}><span className="rank-number">{index + 1}</span><div><b>{game.displayName}</b><small>완료 {data.playStats[game.canonicalId]?.completed ?? 0} · 시작 {data.playStats[game.canonicalId]?.started ?? 0}</small></div><Link href={`/play/local?game=${game.canonicalId}&quick=1`}>바로 다시 하기</Link></li>)}</ol> : <div className="ranking-empty"><strong>첫 판을 기다리고 있어요.</strong><p>게임을 한 번 끝내면 이 기기에서 자주 한 순서가 생깁니다.</p></div>}
    </section>

    {recent.length > 0 && <Shelf title="최근에 한 게임" description="규칙을 다시 찾지 않고 바로 이어가요." items={recent}/>}
    <section className="arcade-section"><header><div><h2>카테고리별 미니게임</h2><p>숫자 맞히기부터 비공개 선택까지, 지금 모인 사람들과 할 게임을 골라요.</p></div><span>{categoryGames.length}개</span></header><div className="arcade-category-filter" role="group" aria-label="게임 카테고리">{(Object.entries(categories) as [ArcadeCategory,(typeof categories)[ArcadeCategory]][]).map(([id,item])=><button key={id} aria-pressed={category===id} onClick={()=>setCategory(id)}>{item.name} <small>{item.ids.length}</small></button>)}</div><div className="arcade-shelf">{categoryGames.map((game)=><ArcadeCard key={game.canonicalId} game={game}/>)}</div></section>

    <section className="arcade-search">
      <header><div><h2>게임 이름으로 찾기</h2><p>익숙한 이름을 검색하면 바로 시작할 수 있어요.</p></div>{matchedGames.length > 0 && <span>{matchedGames.length}개</span>}</header>
      <label className="search"><Icon name="search"/><span className="sr-only">게임 검색</span><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="3·6·9, 마피아, 주사위…"/></label>
      {query.trim() && (matchedGames.length ? <div className="arcade-search-results">{matchedGames.map((game) => <ArcadeCard key={game.canonicalId} game={game} compact/>)}</div> : <div className="ranking-empty"><strong>플레이 가능한 게임에는 없어요.</strong><p>현재는 실제 조작과 결과를 검증한 열한 개만 공개합니다.</p></div>)}
    </section>
  </div>;
}
